import { getSkipUntilLabel, isSkipActive } from '@/utils/queueSkip';
import { t } from '@/locales/translate';
import type { DashboardLiveQueueEntry, DashboardQueueEntryStatus } from './liveQueue/types';
import type { StaffOnlineStatus, StaffRole, TopCarWashStatus } from './types';

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('ru-RU').format(value);
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'KZT',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

export function formatLastUpdated(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  const time = new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);

  if (isToday) {
    return t('common.today', { time });
  }

  const day = new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);

  return `${day}, ${time}`;
}

export function formatGrowthPercent(value: number): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

export function getTopCarWashStatusLabel(status: TopCarWashStatus): string {
  return t(`dashboard.carWashStatus.${status}`);
}

export function formatTimeShort(iso: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

const STAFF_ROLE_KEYS: Record<StaffRole, string> = {
  support: 'dashboard.staffRole.support',
  manager: 'dashboard.staffRole.manager',
  operator: 'dashboard.staffRole.operator',
  ceo: 'dashboard.staffRole.ceo',
  owner: 'dashboard.staffRole.owner',
  client: 'dashboard.staffRole.client',
  'wash-admin': 'dashboard.staffRole.washAdmin',
};

export function getStaffRoleLabel(role: StaffRole): string {
  return t(STAFF_ROLE_KEYS[role]);
}

export function getStaffOnlineStatusLabel(status: StaffOnlineStatus): string {
  return t(`dashboard.onlineStatus.${status}`);
}

export function getDashboardQueueStatusLabel(status: DashboardQueueEntryStatus): string {
  return t(`dashboard.queueStatus.${status}`);
}

export function getDashboardEntryStatusLabel(
  entry: Pick<DashboardLiveQueueEntry, 'status' | 'skipActive' | 'skipUntil'>,
): string {
  if (isSkipActive(entry)) {
    const skipLabel = getSkipUntilLabel(entry.skipUntil);
    if (skipLabel) return skipLabel;
    return t('common.skip.active');
  }
  return getDashboardQueueStatusLabel(entry.status);
}
